import React from 'react';
import { Field, Form, FormikProvider, useFormik } from 'formik';
import * as Yup from 'yup';
import { Button, Label, Input } from 'edozvola-frontend-lib/dist/ui';
import { Col, FormGroup, Row } from 'reactstrap';
import { useParams } from 'react-router-dom';
import { messages } from 'app/config/constants';
import { useDispatch } from 'react-redux';
import { initCaseFormChange, setFormChanged } from 'app/shared/reducers/case-form-change';
import { CaseModeratorsModel } from 'app/edozvola/cases/cases.model';
import { PaginatedDropdown } from 'edozvola-frontend-lib/dist/ui/components/paginated-dropdown';
import { fetchCaseModeratorsQuery, saveCaseModeratorsMutation } from '../service/cases.queries';
import { fetchCosolverForCaseQuery, fetchSolverForCaseQuery } from 'app/edozvola/codebooks/codebooks.queries';
import FormikValueObserver from 'app/shared/formik-observer/formik-observer';
import { Loader } from 'app/shared/loader/loader';
import { getCaseContext } from 'app/shared/util/context-util';

const CaseModeratorsForm = () => {
  const { id } = useParams<{ id: string }>();
  const dispatch = useDispatch();

  const { data: caseModerators, isLoading: caseModeratorsLoading } = fetchCaseModeratorsQuery(id);
  const { data: cosolverOptions, isLoading: cosolverOptionsLoading } = fetchCosolverForCaseQuery(id);
  const { data: solverOptions, isLoading: solverOptionsLoading } = fetchSolverForCaseQuery(id);

  const { mutate: saveCaseModeratorsForm, isPending: savingCaseModeratorsForm } = saveCaseModeratorsMutation(id);

  const validation = Yup.object({
    cosolver: Yup.object().required(messages.MANDATORY_FIELD),
    solver: Yup.object().required(messages.MANDATORY_FIELD),
  });

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: caseModerators || ({} as CaseModeratorsModel),
    validationSchema: validation,
    validateOnMount: true,
    onSubmit: (values: CaseModeratorsModel) => saveCaseModeratorsForm(values),
  });

  const formValid = () => {
    return (
      formik.isValid &&
      formik.values.cosolver?.function &&
      formik.values.cosolver?.workplace &&
      formik.values.cosolver?.email &&
      formik.values.cosolver?.designation &&
      formik.values.solver?.function &&
      formik.values.solver?.workplace &&
      formik.values.solver?.email &&
      formik.values.solver?.designation
    );
  };


  const {sidebarItemPair} = getCaseContext()

  return (
    <>
      {caseModeratorsLoading && <Loader />}
      {!caseModeratorsLoading && (
        <FormikProvider value={formik}>
          <Form>
            <FormikValueObserver
              onChange={dirty => {
                if (dirty.dirty) {
                  dispatch(setFormChanged(true));
                } else {
                  dispatch(initCaseFormChange());
                }
              }}
            />
            <h6 className={'form-header'}>Surješavatelj</h6>
            <Row>
              <Col xs={6}>
                <FormGroup>
                  <Label labelText={'Surješavatelj'} />
                  <Field
                    id="cosolver"
                    name="cosolver"
                    render={({ field }) => (
                      <PaginatedDropdown
                        placeholder={!(cosolverOptionsLoading || sidebarItemPair?.readOnly) && 'Odaberi surješavatelja'}
                        onItemSelect={selectedOption => formik.setFieldValue('cosolver', selectedOption[0])}
                        selected={field.value ?? []}
                        labelKey={'value'}
                        options={cosolverOptions}
                        disabled={cosolverOptionsLoading || sidebarItemPair?.readOnly}
                        error={!(cosolverOptionsLoading || sidebarItemPair?.readOnly) && (formik.errors.cosolver as string)}
                        showErrorMessage
                      />
                    )}
                  />
                </FormGroup>
              </Col>
            </Row>
            <Row>
              <Col xs={3}>
                <FormGroup>
                  <Label labelText={'Radno mjesto'} />
                  <Field
                    id="cosolver.workplace"
                    name="cosolver.workplace"
                    render={({ field }) => (
                      <Input
                        {...field}
                        value={formik.values.cosolver ? field.value ?? '' : ''}
                        disabled={true}
                        error={formik.values.cosolver && !formik.values.cosolver.workplace && messages.MANDATORY_FIELD}
                        type="input"
                      />
                    )}
                  />
                </FormGroup>
              </Col>
              <Col xs={3}>
                <FormGroup>
                  <Label labelText={'Klasifikacija radnog mjesta'} />
                  <Field
                    id="cosolver.function"
                    name="cosolver.function"
                    render={({ field }) => (
                      <Input
                        {...field}
                        value={formik.values.cosolver ? field.value ?? '' : ''}
                        disabled={true}
                        error={formik.values.cosolver && !formik.values.cosolver.function && messages.MANDATORY_FIELD}
                        type="input"
                      />
                    )}
                  />
                </FormGroup>
              </Col>
              <Col xs={3}>
                <FormGroup>
                  <Label labelText={'Titula'} />
                  <Field
                    id="cosolver.title"
                    name="cosolver.title"
                    render={({ field }) => (
                      <Input {...field} value={formik.values.cosolver ? field.value ?? '' : ''} disabled={true} type="input" />
                    )}
                  />
                </FormGroup>
              </Col>
              <Col xs={3}>
                <FormGroup>
                  <Label labelText={'Zvanje'} />
                  <Field
                    id="cosolver.vocation"
                    name="cosolver.vocation"
                    render={({ field }) => (
                      <Input {...field} value={formik.values.cosolver ? field.value ?? '' : ''} disabled={true} type="input" />
                    )}
                  />
                </FormGroup>
              </Col>
            </Row>
            <Row>
              <Col xs={3}>
                <FormGroup>
                  <Label labelText={'Email'} />
                  <Field
                    id="cosolver.email"
                    name="cosolver.email"
                    render={({ field }) => (
                      <Input
                        {...field}
                        value={formik.values.cosolver ? field.value ?? '' : ''}
                        disabled={true}
                        error={formik.values.cosolver && !formik.values.cosolver.email && messages.MANDATORY_FIELD}
                        type="input"
                      />
                    )}
                  />
                </FormGroup>
              </Col>
              <Col xs={3}>
                <FormGroup>
                  <Label labelText={'Telefon'} />
                  <Field
                    id="cosolver.phone"
                    name="cosolver.phone"
                    render={({ field }) => (
                      <Input {...field} value={formik.values.cosolver ? field.value ?? '' : ''} disabled={true} type="input" />
                    )}
                  />
                </FormGroup>
              </Col>
              <Col xs={3}>
                <FormGroup>
                  <Label labelText={'Oznaka'} />
                  <Field
                    id="cosolver.designation"
                    name="cosolver.designation"
                    render={({ field }) => (
                      <Input
                        {...field}
                        value={formik.values.cosolver ? field.value ?? '' : ''}
                        disabled={true}
                        error={formik.values.cosolver && !formik.values.cosolver.designation && messages.MANDATORY_FIELD}
                        type="input"
                      />
                    )}
                  />
                </FormGroup>
              </Col>
            </Row>
            <hr />
            <h6 className={'form-header'}>Rješavatelj</h6>
            <Row>
              <Col xs={6}>
                <FormGroup>
                  <Label labelText={'Rješavatelj'} />
                  <Field
                    id="solver"
                    name="solver"
                    render={({ field }) => (
                      <PaginatedDropdown
                        placeholder={!(solverOptionsLoading || sidebarItemPair?.readOnly) && 'Odaberi rješavatelja'}
                        onItemSelect={selectedOption => formik.setFieldValue('solver', selectedOption[0])}
                        selected={field.value ?? []}
                        labelKey={'value'}
                        options={solverOptions}
                        disabled={solverOptionsLoading || sidebarItemPair?.readOnly}
                        error={!(solverOptionsLoading || sidebarItemPair?.readOnly) && (formik.errors.solver as string)}
                        showErrorMessage
                      />
                    )}
                  />
                </FormGroup>
              </Col>
            </Row>
            <Row>
              <Col xs={3}>
                <FormGroup>
                  <Label labelText={'Radno mjesto'} />
                  <Field
                    id="solver.workplace"
                    name="solver.workplace"
                    render={({ field }) => (
                      <Input
                        {...field}
                        value={formik.values.solver ? field.value ?? '' : ''}
                        disabled={true}
                        error={formik.values.solver && !formik.values.solver.workplace && messages.MANDATORY_FIELD}
                        type="input"
                      />
                    )}
                  />
                </FormGroup>
              </Col>
              <Col xs={3}>
                <FormGroup>
                  <Label labelText={'Klasifikacija radnog mjesta'} />
                  <Field
                    id="solver.function"
                    name="solver.function"
                    render={({ field }) => (
                      <Input
                        {...field}
                        value={formik.values.solver ? field.value ?? '' : ''}
                        disabled={true}
                        error={formik.values.solver && !formik.values.solver.function && messages.MANDATORY_FIELD}
                        type="input"
                      />
                    )}
                  />
                </FormGroup>
              </Col>
              <Col xs={3}>
                <FormGroup>
                  <Label labelText={'Titula'} />
                  <Field
                    id="solver.title"
                    name="solver.title"
                    render={({ field }) => (
                      <Input {...field} value={formik.values.solver ? field.value ?? '' : ''} disabled={true} type="input" />
                    )}
                  />
                </FormGroup>
              </Col>
              <Col xs={3}>
                <FormGroup>
                  <Label labelText={'Zvanje'} />
                  <Field
                    id="solver.vocation"
                    name="solver.vocation"
                    render={({ field }) => (
                      <Input {...field} value={formik.values.solver ? field.value ?? '' : ''} disabled={true} type="input" />
                    )}
                  />
                </FormGroup>
              </Col>
            </Row>
            <Row>
              <Col xs={3}>
                <FormGroup>
                  <Label labelText={'Email'} />
                  <Field
                    id="solver.email"
                    name="solver.email"
                    render={({ field }) => (
                      <Input
                        {...field}
                        value={formik.values.solver ? field.value ?? '' : ''}
                        disabled={true}
                        error={formik.values.solver && !formik.values.solver.email && messages.MANDATORY_FIELD}
                        type="input"
                      />
                    )}
                  />
                </FormGroup>
              </Col>
              <Col xs={3}>
                <FormGroup>
                  <Label labelText={'Telefon'} />
                  <Field
                    id="solver.phone"
                    name="solver.phone"
                    render={({ field }) => (
                      <Input {...field} value={formik.values.solver ? field.value ?? '' : ''} disabled={true} type="input" />
                    )}
                  />
                </FormGroup>
              </Col>
              <Col xs={3}>
                <FormGroup>
                  <Label labelText={'Oznaka'} />
                  <Field
                    id="solver.designation"
                    name="solver.designation"
                    render={({ field }) => (
                      <Input
                        {...field}
                        value={formik.values.solver ? field.value ?? '' : ''}
                        disabled={true}
                        error={formik.values.solver && !formik.values.solver.designation && messages.MANDATORY_FIELD}
                        type="input"
                      />
                    )}
                  />
                </FormGroup>
              </Col>
            </Row>
            {!sidebarItemPair?.readOnly && (
              <div className="case-save-button">
                <Button
                  color={'info'}
                  label={'Spremi'}
                  size={'lg'}
                  isLoading={savingCaseModeratorsForm}
                  onClick={formik.submitForm}
                  disabled={!formik.dirty || !formValid()}
                />
              </div>
            )}
          </Form>
        </FormikProvider>
      )}
    </>
  );
};

export default CaseModeratorsForm;
